import { Component, Input, OnInit } from "@angular/core";
import { BackendApiService } from "../../../../app/services/backend-api.service";
import { CommonModule } from "@angular/common";
import { UptimeChartsComponent } from "./uptime-chart/uptime-chart.component";
import { BaseUrlConstantsService } from "../../../../app/services/base-url-constants.service";
import { MatButtonModule } from "@angular/material/button";

@Component({
  selector: "uptime-charts-container",
  templateUrl: "./uptime-charts-container.component.html",
  styleUrls: ["./uptime-charts-container.component.scss"],
  standalone: true,
  imports: [CommonModule, UptimeChartsComponent, MatButtonModule],
})
export class UptimeChartContainerComponent implements OnInit {
  chartSeries: any[] = [];
  iotChartSeries: any = {};
  kurtosisChartSeries: any = {};
  dbChartSeries = {};
  serviceChartSeries = {};
  selected_filter = "day";

  serviceChartLoader = false;
  private uptime_base_url = BaseUrlConstantsService.BASE_UPTIME_SERVICE;

  @Input() type = "";
  // Service cards configuration
  //   services = [
  //     {
  //       name: 'MQTT',
  //       description: 'Message Queue Service',
  //       color: '#6366f1', // Purple
  //       uptime: 99.95,
  //       status: 'healthy',
  //       gauges: [
  //         { label: 'Throughput', value: 85 },
  //         { label: 'Latency', value: 92 }
  //       ]
  //     },
  //     {
  //       name: 'Kurtosis',
  //       description: 'Statistical Analysis',
  //       color: '#f97316', // Orange
  //       uptime: 99.87,
  //       status: 'healthy',
  //       gauges: [
  //         { label: 'Processing', value: 78 },
  //         { label: 'Memory', value: 65 }
  //       ]
  //     },
  //     {
  //       name: 'Combined Data',
  //       description: 'Aggregated Metrics',
  //       color: '#8b5cf6', // Purple variant
  //       uptime: 99.92,
  //       status: 'healthy',
  //       gauges: [
  //         { label: 'Sync Rate', value: 94 },
  //         { label: 'Accuracy', value: 88 }
  //       ]
  //     },
  //     {
  //       name: 'IoT Services',
  //       description: 'Device Management',
  //       color: '#10b981', // Green
  //       uptime: 99.99,
  //       status: 'healthy',
  //       gauges: [
  //         { label: 'Devices', value: 96 },
  //         { label: 'Network', value: 89 },
  //         { label: 'Security', value: 95 },
  //         { label: 'Performance', value: 91 },
  //         { label: 'Storage', value: 83 },
  //         { label: 'Analytics', value: 87 }
  //       ]
  //     }
  //   ];

  //   currentMonth = 'September';
  //   dateRange = '1st September - 24th September';

  constructor(private srv: BackendApiService) {}

  ngOnInit(): void {
    this.chartSeries = [];
    this.iotChartSeries = [];
    this.dbChartSeries = [];
    this.serviceChartSeries = [];
    this.kurtosisChartSeries = [];
    if (this.type == "application") {
      this.fetchCharts(); //mqtt
      this.fetchIotCharts();
      this.fetchKurtosisCharts();
    } else {
      this.fetchDbCharts();
      this.fetchServiceCharts();
    }
  }

  async fetchCharts() {
    try {
      const res: any = await this.srv.getDataV2(
        // `http://localhost:7776/uptime/trends/${this.selected_filter}/mqtt/application`
        `${this.uptime_base_url}/uptime/trends/${this.selected_filter}/mqtt/application`
      );
      if (res?.data) {
        this.chartSeries = this.transformResponse(res.data);
      }
    } catch (error) {
      console.error("Error fetching uptime data", error);
    }
  }

  private transformResponse(data: any): any[] {
    const series: any[] = [];

    const nameMap: Record<string, string> = {
      "OS-API": "Operating System",
      "MQTT-SERVICE": "Service",
      "mos-mqtt": "Processing",
      KURTOSIS_SERVICE: "Service",
      KURTOSIS: "Processing",
    };
    console.log(data);
    // iterate over all keys (os_data, service_data, processing_data, etc.)
    Object.keys(data).forEach((key) => {
      const arr = data[key];
      if (Array.isArray(arr) && arr.length > 0) {
        const rawName = arr[0].name;
        const friendlyName = nameMap[rawName] || rawName; // fallback to raw if no mapping

        series.push({
          name: friendlyName,
          data: arr
            .map((item) => ({
              x: item.date,
              y: isNaN(item.uptime_percentage)
                ? null
                : parseFloat(item.uptime_percentage),
            }))
            .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()), // ensure ascending date order
        });
      }
    });
    console.log(series);
    return series;
  }

  async fetchIotCharts() {
    try {
      const res: any = await this.srv.getDataV2(
        // "http://localhost:7776/uptime/iot-trends/%{this.selected_filter}/iot/application"
        `${this.uptime_base_url}/uptime/iot-trends/${this.selected_filter}/iot/application`
      );
      if (res?.data) {
        this.iotChartSeries = this.transformIotResponse(res.data);
      }
    } catch (error) {
      console.error("Error fetching uptime data", error);
    }
  }

  private transformIotResponse(data: any): any[] {
    const series: any[] = [];
    Object.keys(data).forEach((instanceId) => {
      const instanceData = data[instanceId];

      Object.keys(instanceData).forEach((categoryKey) => {
        // skip the `name` property
        if (categoryKey === "name") return;

        const arr = instanceData[categoryKey];
        if (Array.isArray(arr) && arr.length > 0) {
          const rawName = arr[0].name || instanceData["name"]; // take name from first entry
          const friendlyName = `${rawName}`; // include instanceId for uniqueness

          series.push({
            name: friendlyName,
            data: arr
              .map((item) => ({
                x: item.date,
                y: isNaN(item.uptime_percentage)
                  ? null
                  : parseFloat(item.uptime_percentage),
              }))
              .sort(
                (a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()
              ),
          });
        }
      });
    });
    console.log(series);
    return series;
  }

  async fetchKurtosisCharts() {
    try {
      const res: any = await this.srv.getDataV2(
        // "http://localhost:7776/uptime/trends/month/kurtosis/application"
        `${this.uptime_base_url}/uptime/trends/${this.selected_filter}/kurtosis/application`
      );
      if (res?.data) {
        this.kurtosisChartSeries = this.transformResponse(res.data);
      }
    } catch (error) {
      console.error("Error fetching uptime data", error);
    }
  }

  async fetchDbCharts() {
    try {
      const res: any = await this.srv.getDataV2(
        // "http://localhost:7776/uptime/db-trends/month"
        `${this.uptime_base_url}/uptime/db-trends/month`
      );
      if (res?.data) {
        this.dbChartSeries = this.transformIotResponse(res.data);
      }
    } catch (error) {
      console.error("Error fetching uptime data", error);
    }
  }
  async fetchServiceCharts() {
    try {
      this.serviceChartLoader = true;
      const res: any = await this.srv.getDataV2(
        // "http://localhost:7776/uptime/service-trends/month"
        `${this.uptime_base_url}/uptime/service-trends/${this.selected_filter}`
      );
      if (res?.data) {
        this.serviceChartLoader = false;
        this.serviceChartSeries = this.transformIotResponse(res.data);
      }
    } catch (error) {
      console.error("Error fetching uptime data", error);
    }
  }

  changeFilter(filter) {
    if (filter == this.selected_filter) return;
    this.selected_filter = filter;
    this.ngOnInit();
  }
}
